import React, { useState, useEffect } from 'react';
import { Linkedin, Github, Instagram, Sparkles, Zap } from 'lucide-react';
import { personalInfo, socialLinks } from '../data/mock';

const iconMap = { Linkedin, Github, Instagram };

// Docker Buddy mascot
const DockerWhale = ({ style }) => (
  <div className="absolute animate-float" style={style}>
    <div className="relative">
      <div className="text-5xl transform hover:scale-110 transition-transform cursor-pointer">
        🐳
      </div>
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-400 rounded-full animate-ping" />
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
        Docker Buddy
      </div>
    </div>
  </div>
);

// K8s Guardian mascot
const K8sRobot = ({ style }) => (
  <div className="absolute animate-float" style={{ ...style, animationDelay: '1s' }}>
    <div className="relative">
      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
        <span className="text-3xl">⚙️</span>
      </div>
      <div className="absolute -top-2 -left-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center text-xs animate-bounce">
        ✓
      </div>
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
        K8s Guardian
      </div>
    </div>
  </div>
);

// IaC Cube mascot
const TerraformCube = ({ style }) => (
  <div className="absolute animate-float" style={{ ...style, animationDelay: '1.5s' }}>
    <div className="relative">
      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-lg" style={{ transform: 'rotate(15deg)' }}>
        <span className="text-2xl">🧊</span>
      </div>
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
        IaC Cube
      </div>
    </div>
  </div>
);

const HeroSection = () => {
  const [currentTitle, setCurrentTitle] = useState(0);

  useEffect(() => {
    const titleInterval = setInterval(() => {
      setCurrentTitle((prev) => (prev + 1) % personalInfo.titles.length);
    }, 3000);
    return () => clearInterval(titleInterval);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-b from-blue-100 via-purple-50 to-pink-100">
      {/* Animated Background - Clouds and DevOps Mascots */}
      <div className="absolute inset-0">
        {/* Light pastel clouds */}
        <div className="absolute top-20 left-10 text-8xl animate-cloud opacity-40">☁️</div>
        <div className="absolute top-40 right-20 text-6xl animate-cloud-slow opacity-30">☁️</div>
        <div className="absolute bottom-60 left-1/4 text-7xl animate-cloud opacity-35">☁️</div>
        
        {/* DevOps Mascots */}
        <DockerWhale style={{ top: '15%', left: '5%' }} />
        <K8sRobot style={{ top: '25%', right: '8%' }} />
        <TerraformCube style={{ bottom: '40%', right: '5%' }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Hero Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur shadow-lg mb-6 animate-bounce-slow">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              <span className="font-bold text-purple-600">DevSecOps Superhero</span>
              <Sparkles className="w-5 h-5 text-yellow-500" />
            </div>

            {/* Animated Title */}
            <div className="h-20 mb-4">
              <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 animate-pulse-slow">
                {personalInfo.titles[currentTitle]}
              </h2>
            </div>

            {/* Name */}
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              <span className="text-gray-800">I'm </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient">
                {personalInfo.name}
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-xl text-gray-600 mb-8 font-medium">
              Securing Kubernetes at scale. Automating resilient cloud infrastructure. Engineering zero-trust cloud systems.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
              <button
                onClick={() => document.querySelector('#skills')?.scrollIntoView({ behavior: 'smooth' })}
                className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2"
              >
                <Zap className="w-5 h-5 group-hover:animate-spin" />
                See My Powers!
              </button>
              <button
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white rounded-full text-purple-600 font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all border-2 border-purple-200"
              >
                Let's Team Up! 🤝
              </button>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 justify-center lg:justify-start">
              {socialLinks.map((link) => {
                const Icon = iconMap[link.icon];
                return (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-purple-600 hover:scale-110 hover:rotate-6 transition-all border-2 border-purple-100"
                  >
                    {Icon && <Icon className="w-6 h-6" />}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Right - Hero Avatar */}
          <div className="relative flex justify-center">
            {/* Avatar Container - Clean, static profile image */}
            <div className="relative w-[332px] h-[332px] md:w-[416px] md:h-[416px] rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-2 shadow-2xl">
              <div className="w-full h-full rounded-lg bg-white overflow-hidden border-4 border-yellow-300 relative">
                <img
                  src={personalInfo.profileImage}
                  alt={personalInfo.name}
                  className="w-full h-full object-cover"
                />
                {/* Status Badge - bottom left corner */}
                <div className="absolute bottom-3 left-3 px-3 py-2 bg-gray-900/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-700/50">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-50 animate-pulse"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]"></span>
                    </span>
                    <span className="text-xs font-semibold text-green-400 tracking-wide">ACTIVE</span>
                  </div>
                  <div className="text-[11px] text-gray-300 font-medium">DevOps Engineer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
